  (function () {
    'use strict';
    // One copy per page. This code used to live in the section's
    // {% javascript %} tag, which Shopify bundles once per page. As an asset
    // it loads from a script tag that renders once per section instance, so
    // a second buy box or an editor re-render must not register the
    // document listeners again.
    if (window.__eroPdpBuyBoxLoaded) return;
    window.__eroPdpBuyBoxLoaded = true;

    const THUMB_PRESS_MS = 160;
    // One clock for every flavor/plan swap: the JS text crossfade, the
    // gallery crossfade and the CSS --bb-swap transitions all run at this.
    const SWAP_MS = 200;

    // logoFilter names the inline SVG filter for the lockup glow. It cannot
    // be driven by the accent custom property, because SVG feFlood colour
    // does not read CSS variables reliably.
    // `host` is the entry for a product that matches none of the three pins.
    // It keeps the brand cyan so an unpinned page has a defined accent rather
    // than failing selectFlavor()'s accent guard and rendering nothing.
    const ACCENTS = {
      br: { color: '#00B8D4', rgb: '0, 184, 212', logoFilter: 'eroLogoGlowCyan' },
      pm: { color: '#F3A363', rgb: '243, 163, 99', logoFilter: 'eroLogoGlowPeach' },
      bundle: { color: '#9B6DFF', rgb: '155, 109, 255', logoFilter: 'eroLogoGlowPurple' },
      host: { color: '#00B8D4', rgb: '0, 184, 212', logoFilter: 'eroLogoGlowCyan' }
    };

    const money = (cents) => '$' + (Number(cents || 0) / 100).toFixed(2);

    const perServing = (price, servings) => {
      const count = Number(servings || 0);
      if (count <= 0) return '';
      return '$' + (Number(price || 0) / count / 100).toFixed(2) + ' / serving';
    };

    const isActivationKey = (event) => event.key === 'Enter' || event.key === ' ';

    function parseCatalog(root) {
      const dataNode = root.querySelector('[data-ero-pdp-products]');
      if (!dataNode) return null;

      try {
        return JSON.parse(dataNode.textContent);
      } catch (error) {
        console.error('ERO PDP buy box product data could not be parsed.', error);
        return null;
      }
    }

    function initRoot(root) {
      if (root._eroBuyBoxCleanup) return;

      const catalog = parseCatalog(root);
      if (!catalog || !catalog.products) return;

      const products = catalog.products;
      // Free mode replaces the one-time price with a word, so every write to
      // that node has to route around money().
      const FREE_MODE = catalog.free === true;
      const ATC_LABEL = catalog.atc_label || 'ADD TO BAG';

      // Liquid resolved the hosting product and named its key here. The
      // guard used to be `!catalog.products.br`, which meant any page whose
      // buy box had no Blue Raspberry pin returned before initialising at
      // all. Falling back to 'br' keeps the shipped behaviour for a root
      // that predates the attribute.
      const initialFlavor = (function () {
        const declared = root.dataset.initialFlavor;
        if (declared && products[declared] && ACCENTS[declared]) return declared;
        if (products.br) return 'br';
        return '';
      })();
      if (!initialFlavor) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const nameNode = root.querySelector('[data-product-name]');
      const variantInput = root.querySelector('[data-variant-id]');
      const sellingPlanInput = root.querySelector('[data-selling-plan]');
      const subscribeTitle = root.querySelector('[data-subscribe-title]');
      const subscribeStruck = root.querySelector('[data-subscribe-struck]');
      const subscribePrice = root.querySelector('[data-subscribe-price]');
      const subscribeServing = root.querySelector('[data-subscribe-serving]');
      const onetimePrice = root.querySelector('[data-onetime-price]');
      const onetimeServing = root.querySelector('[data-onetime-serving]');
      const bundleBadge = root.querySelector('[data-bundle-badge]');
      const atc = root.querySelector('[data-atc]');
      const atcLabels = Array.from(root.querySelectorAll('[data-atc-label]'));
      const flavorButtons = Array.from(root.querySelectorAll('[data-flavor]'));
      const planButtons = Array.from(root.querySelectorAll('[data-plan]'));
      const subscribeButton = root.querySelector('[data-plan="subscribe"]');
      const freqList = root.querySelector('[data-frequency-list]');
      const track = root.querySelector('[data-gallery-track]');
      const viewport = root.querySelector('[data-gallery-viewport]');
      const thumbsNode = root.querySelector('[data-gallery-thumbs]');
      const railFrame = root.querySelector('[data-thumbs-frame]');
      const railPrev = root.querySelector('[data-rail-prev]');
      const railNext = root.querySelector('[data-rail-next]');
      const gallery = root.querySelector('[data-gallery]');
      const qtyInput = root.querySelector('[data-qty-input]');
      const qtyValue = root.querySelector('[data-qty-value]');
      const qtyButtons = Array.from(root.querySelectorAll('[data-qty-step]'));
      const zoomButton = root.querySelector('[data-zoom]');
      const logoGlow = root.querySelector('[data-logo-glow]');
      const logoGlowMobile = root.querySelector('[data-logo-glow-mobile]');
      const lightbox = root.querySelector('[data-bb-lightbox]');
      const lightboxImage = lightbox && lightbox.querySelector('[data-bb-lightbox-image]');
      const lightboxClose = lightbox && lightbox.querySelector('[data-bb-lightbox-close]');
      const ratingRow = root.querySelector('[data-rating-row]');
      const ratingValueNode = root.querySelector('[data-rating-value]');
      const starClips = Array.from(root.querySelectorAll('[data-star-clip]'));
      const LOOX = catalog.loox || {};
      let ratingToken = 0;

      function paintRating(value) {
        const rating = Math.max(0, Math.min(5, Number(value) || 0));
        const text = rating.toFixed(1);
        if (ratingValueNode) ratingValueNode.textContent = text;
        if (ratingRow) ratingRow.setAttribute('aria-label', 'Rated ' + text + ' out of 5');
        starClips.forEach(function (rect, index) {
          const fill = Math.max(0, Math.min(1, rating - index));
          const width = fill >= 1 ? 14 : (fill <= 0 ? 0 : 1.6 + 10.8 * fill);
          rect.setAttribute('width', String(Math.round(width * 1000) / 1000));
        });
      }

      function clearRatingPending() {
        if (ratingRow) ratingRow.classList.remove('is-rating-pending');
      }

      // Rating source per key. Blue Raspberry, Peach Mango and the Sample
      // Pack use their own Loox rating. The Bundle is always Blue Raspberry
      // plus Peach Mango combined and rounded. An unpinned host follows the
      // rating_source setting: 'all' reads the all-product bottomline
      // (Shaker, Hat), 'product' reads its own id.
      function ratingRequest(key) {
        const api = window.EroLoox;
        if (!api) return Promise.resolve(null);
        if (key === 'bundle') return api.combinedRating(LOOX.combined_ids);
        if (key === 'host' && LOOX.rating_source === 'all') return api.allProductsRating();
        const entry = products[key];
        return api.productRating(entry && entry.loox_id);
      }

      // Token guard: a slow answer for a flavor the shopper already left is
      // ignored. A null answer paints the fallback rating.
      function updateRating(key) {
        const token = ++ratingToken;
        ratingRequest(key).then(function (result) {
          if (token !== ratingToken) return;
          paintRating(result ? result.rating : LOOX.rating_fallback);
          clearRatingPending();
        });
      }
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const timers = new Set();
      let quantity = 1;
      let activeIndex = 0;
      let lightboxIndex = 0;
      let pricesPainted = false;
      let activeFlavor = initialFlavor;
      // Last value scheduled for each price node, so the equality guard in
      // updatePrices() survives swaps that overlap the 200ms crossfade.
      const priceTargets = new Map();

      // Tracked so the cleanup can cancel anything still pending when the
      // theme editor swaps the section out mid-transition.
      function later(callback, delay) {
        const id = window.setTimeout(function () {
          timers.delete(id);
          callback();
        }, delay);
        timers.add(id);
        return id;
      }

      const wantsMotion = () => !reducedMotionQuery.matches;

      // ── Review card (8.2) ─────────────────────────────────────────────
      // Live Loox reviews in the buy box card. The section renders one
      // fallback slide from its settings; this replaces it with up to four
      // live slides that share the same grid cell and crossfade on the CSS
      // opacity transition. Any failure leaves the fallback slide showing.
      const REVIEW_HOLD_MS = 6000;
      const REVIEW_FADE_MS = 400;
      const REVIEW_MAX = 4;
      const REVIEW_MAX_CHARS = 128;
      const reviewCard = root.querySelector('[data-review-card]');
      const reviewStack = reviewCard ? reviewCard.querySelector('[data-review-stack]') : null;
      const reviewNext = reviewCard ? reviewCard.querySelector('[data-review-next]') : null;
      const reviewFallback = reviewStack ? reviewStack.querySelector('[data-review-slide]') : null;
      const reviewMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const reviewSets = new Map();
      let reviewSlides = reviewFallback ? [reviewFallback] : [];
      let reviewIndex = 0;
      let reviewKey = '';
      let reviewToken = 0;
      let reviewTimer = 0;
      let reviewHover = false;
      let reviewTouch = false;
      let reviewOffscreen = false;
      let reviewObserver = null;
      let reviewsWarmed = false;

      function cleanReviewBody(text) {
        return String(text || '').replace(/\s+/g, ' ').trim();
      }

      function reviewInitials(name) {
        const words = String(name || '').replace(/\./g, '').trim().split(/\s+/).filter(Boolean);
        if (!words.length) return '';
        const first = words[0].charAt(0);
        const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
      }

      function reviewSourceIds(key) {
        const combined = Array.isArray(LOOX.combined_ids) ? LOOX.combined_ids : [];
        if (key === 'bundle') return combined;
        if (key === 'host' && LOOX.review_source === 'combined') return combined;
        const entry = products[key];
        return entry && entry.loox_id ? [entry.loox_id] : [];
      }

      // Published, read-only, so the PDP Reviews section shows the same
      // Loox source this buy box does for the CURRENT selection. A copy is
      // returned so no caller can mutate the catalog's id list. Selection
      // changes are already announced by ero:buybox:change.
      root._eroReviewSourceIds = () => reviewSourceIds(activeFlavor).slice();

      // Single product: Loox featured order. Combined: newest first across
      // both flavors (Taylor, Sep 28, 2026). Five-star, body 128 characters
      // or fewer. A failed request is not cached, so a later visit retries.
      function fetchReviews(key) {
        if (reviewSets.has(key)) return Promise.resolve(reviewSets.get(key));
        const api = window.EroLoox;
        const ids = reviewSourceIds(key);
        if (!api || typeof api.productReviews !== 'function' || !ids.length) return Promise.resolve(null);
        const combined = ids.length > 1;
        return Promise.all(ids.map(function (id) {
          return api.productReviews(id, { rating: 5, sort: combined ? 'date' : 'featured', limit: 100 });
        })).then(function (lists) {
          if (lists.some(function (list) { return !Array.isArray(list); })) return null;
          const list = [].concat.apply([], lists).filter(function (review) {
            const body = cleanReviewBody(review && review.body);
            return review && Number(review.rating) === 5 && body.length > 0 && body.length <= REVIEW_MAX_CHARS;
          });
          if (combined) {
            list.sort(function (a, b) {
              return (Date.parse(b.date || b.createdAt) || 0) - (Date.parse(a.date || a.createdAt) || 0);
            });
          }
          reviewSets.set(key, list);
          return list;
        });
      }

      // Loox photo URLs point at the full original (~750 KB, 1440 x 1080).
      // The same path without "_orig" is Loox's own 375 x 500 copy (~20 KB),
      // checked Sep 28, 2026. Load the small copy; if it fails, try the
      // original once; if that fails too, drop the img so the initials show.
      function reviewPhotoUrls(review) {
        const photo = (Array.isArray(review.media) ? review.media : []).find(function (item) {
          return item && item.type === 'photo' && (item.thumbnailUrl || item.url);
        });
        if (!photo) return null;
        const full = photo.url || photo.thumbnailUrl;
        const small = photo.thumbnailUrl || full.replace(/_orig(\.[a-z0-9]+)(\?.*)?$/i, '$1$2');
        return { small: small, full: full };
      }

      function buildReviewSlide(review) {
        const name = (review.reviewer && review.reviewer.name) || '';
        const slide = document.createElement('div');
        slide.className = 'bb-quote__slide';
        slide.setAttribute('data-review-slide', '');
        slide.setAttribute('aria-hidden', 'true');

        const media = document.createElement('div');
        media.className = 'bb-quote__media';
        media.setAttribute('aria-hidden', 'true');
        const initials = document.createElement('span');
        initials.className = 'bb-quote__initials';
        initials.textContent = reviewInitials(name);
        media.appendChild(initials);

        const urls = reviewPhotoUrls(review);
        if (urls) {
          const img = document.createElement('img');
          img.className = 'bb-quote__photo';
          img.alt = '';
          img.decoding = 'async';
          img.loading = 'lazy';
          let triedFull = urls.small === urls.full;
          img.addEventListener('error', function () {
            if (!triedFull) {
              triedFull = true;
              img.src = urls.full;
              return;
            }
            img.remove();
          });
          img.src = urls.small;
          media.appendChild(img);
        }

        const col = document.createElement('div');
        col.className = 'bb-quote__col';
        const text = document.createElement('p');
        text.className = 'bb-quote__text';
        text.textContent = '“' + cleanReviewBody(review.body) + '”';
        const attr = document.createElement('div');
        attr.className = 'bb-quote__attr';
        const author = document.createElement('span');
        author.className = 'bb-quote__author';
        author.textContent = name;
        const verified = document.createElement('span');
        verified.className = 'bb-quote__verified';
        verified.textContent = 'VERIFIED BUYER';
        verified.hidden = review.verified !== true;
        attr.append(author, verified);
        col.append(text, attr);
        slide.append(media, col);
        return slide;
      }

      function reviewTextOverflows(slide) {
        const text = slide.querySelector('.bb-quote__text');
        return !!text && text.scrollHeight > text.clientHeight + 1;
      }

      function showReview(index) {
        if (!reviewSlides.length) return;
        let next = ((index % reviewSlides.length) + reviewSlides.length) % reviewSlides.length;
        // Checked again at display time: a breakpoint change can shrink the box.
        while (reviewSlides.length > 1 && reviewSlides[next] !== reviewFallback && reviewTextOverflows(reviewSlides[next])) {
          reviewSlides[next].remove();
          reviewSlides.splice(next, 1);
          next = next % reviewSlides.length;
        }
        reviewSlides.forEach(function (slide, i) {
          const on = i === next;
          slide.classList.toggle('is-current', on);
          slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
        reviewIndex = next;
        if (reviewNext) reviewNext.hidden = reviewSlides.length < 2;
      }

      function reviewPaused() {
        return reviewHover || reviewTouch || reviewOffscreen || document.hidden;
      }

      function scheduleReview() {
        window.clearTimeout(reviewTimer);
        reviewTimer = 0;
        if (reviewSlides.length < 2 || reviewMotion.matches) return;
        reviewTimer = window.setTimeout(tickReview, REVIEW_HOLD_MS);
      }

      // Same pause behaviour as the approved mockup: while paused, re-check
      // every 250ms, then advance.
      function tickReview() {
        if (reviewPaused()) {
          reviewTimer = window.setTimeout(tickReview, 250);
          return;
        }
        showReview(reviewIndex + 1);
        scheduleReview();
      }

      function applyReviews(list) {
        if (!reviewStack) return;
        const outgoing = reviewSlides[reviewIndex] || null;
        const kept = [];
        (Array.isArray(list) ? list : []).some(function (review) {
          const slide = buildReviewSlide(review);
          reviewStack.appendChild(slide);
          if (reviewTextOverflows(slide)) {
            slide.remove();
          } else {
            kept.push(slide);
          }
          return kept.length >= REVIEW_MAX;
        });
        if (!kept.length && reviewFallback) {
          if (!reviewFallback.isConnected) reviewStack.appendChild(reviewFallback);
          kept.push(reviewFallback);
        }
        // Slides from the old set leave at once, except the one on screen,
        // which fades out under the new first slide and then leaves.
        reviewSlides.forEach(function (slide) {
          if (slide !== outgoing && kept.indexOf(slide) === -1) slide.remove();
        });
        if (outgoing && kept.indexOf(outgoing) === -1) {
          outgoing.classList.remove('is-current');
          outgoing.setAttribute('aria-hidden', 'true');
          const leaveTimer = window.setTimeout(function () {
            timers.delete(leaveTimer);
            if (reviewSlides.indexOf(outgoing) === -1) outgoing.remove();
          }, REVIEW_FADE_MS + 50);
          timers.add(leaveTimer);
        }
        reviewSlides = kept;
        reviewIndex = 0;
        showReview(0);
        scheduleReview();
      }

      function updateReviews(key) {
        if (!reviewStack || key === reviewKey) return;
        reviewKey = key;
        const token = ++reviewToken;
        const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
        Promise.all([fetchReviews(key), fontsReady]).then(function (results) {
          if (token !== reviewToken) return;
          applyReviews(results[0]);
          // Bag PDP: fetch the other two flavor sets in the background so a
          // flavor swap crossfades straight from cache.
          if (!reviewsWarmed && products.bundle) {
            reviewsWarmed = true;
            ['br', 'pm', 'bundle'].forEach(function (other) {
              if (other !== key && products[other]) fetchReviews(other);
            });
          }
        }).catch(function () {
          if (token !== reviewToken) return;
          applyReviews(null);
        });
      }

      if (reviewCard) {
        reviewCard.addEventListener('pointerenter', function (event) {
          if (event.pointerType === 'mouse') reviewHover = true;
        }, { signal: signal });
        reviewCard.addEventListener('pointerleave', function (event) {
          if (event.pointerType === 'mouse') reviewHover = false;
        }, { signal: signal });
        reviewCard.addEventListener('pointerdown', function (event) {
          if (event.pointerType !== 'mouse') reviewTouch = true;
        }, { signal: signal });
        reviewCard.addEventListener('pointerup', function (event) {
          if (event.pointerType !== 'mouse') reviewTouch = false;
        }, { signal: signal });
        reviewCard.addEventListener('pointercancel', function () {
          reviewTouch = false;
        }, { signal: signal });
        if ('IntersectionObserver' in window) {
          reviewObserver = new IntersectionObserver(function (entries) {
            reviewOffscreen = !entries[entries.length - 1].isIntersecting;
          });
          reviewObserver.observe(reviewCard);
        }
      }

      if (reviewNext) {
        reviewNext.hidden = true;
        reviewNext.addEventListener('click', function () {
          showReview(reviewIndex + 1);
          scheduleReview();
        }, { signal: signal });
      }

      reviewMotion.addEventListener('change', scheduleReview, { signal: signal });

      // The section root carries isolation: isolate, which would trap this
      // fixed overlay inside the section's stacking context and paint it
      // under the nav. Reparenting to body is the escape; the cleanup below
      // removes it so a theme-editor reload cannot leave orphans behind.
      if (lightbox) document.body.appendChild(lightbox);
      let activePlan = root.dataset.defaultPlan === 'one_time' ? 'onetime' : 'subscribe';
      // The chosen selling plan id, as a string (ids cross the Liquid
      // boundary as strings). Starts from the server-rendered selection.
      const initialFreqButton = freqList ? freqList.querySelector('[role="radio"][aria-checked="true"]') : null;
      let activePlanId = initialFreqButton ? initialFreqButton.dataset.planId : null;
      let galleryApi = null;
      let motionDuration = 0.35;
      let motionMedia = null;
      const pressTimers = new Map();

      if (window.gsap && typeof window.gsap.matchMedia === 'function') {
        motionMedia = window.gsap.matchMedia();
        motionMedia.add('(prefers-reduced-motion: reduce)', function () {
          motionDuration = 0;
          return function () {
            motionDuration = 0.35;
          };
        });
      }

      const activeProduct = () => products[activeFlavor];
      const FREQ_MATCH = String(catalog.default_frequency || '').trim().toLowerCase();
      const FREQ_POPULAR = String(catalog.frequency_popular_label || '').trim();
      const allocationsOf = (productData) => (
        productData && Array.isArray(productData.selling_plan_allocations)
          ? productData.selling_plan_allocations
          : []
      );
      // The plan whose frequency matches the Default delivery frequency
      // setting, falling back to the first plan. Never positional alone,
      // so reordering plans in Shopify admin cannot change the default.
      const defaultAllocation = (productData) => {
        const list = allocationsOf(productData);
        if (!list.length) return null;
        const match = FREQ_MATCH
          ? list.find((a) => String(a.label || '').toLowerCase().indexOf(FREQ_MATCH) !== -1)
          : null;
        return match || list[0];
      };
      // Every plan read goes through here: prices, badge, hidden input,
      // ATC label and the ero:buybox:change event.
      const activeAllocation = (productData) => {
        const list = allocationsOf(productData);
        return list.find((a) => a.id === activePlanId) || defaultAllocation(productData);
      };

      // Rebuilds the frequency buttons from the product's own plans so a
      // product with different plans can never show a stale set, keeps the
      // chosen plan when the product has it, and tags the default frequency.
      // Same structure as the Liquid: a label span, plus separator and tag
      // spans on the default plan only. Roving tabindex: only the selected
      // button is in the tab order.
      function renderFrequencyOptions(productData) {
        if (!freqList) return;
        const list = allocationsOf(productData);
        const fallback = defaultAllocation(productData);
        const current = activeAllocation(productData);
        if (current) activePlanId = current.id;
        freqList.textContent = '';
        list.forEach((a) => {
          const button = document.createElement('span');
          const selected = !!current && a.id === current.id;
          button.className = 'bb-freq-btn ero-picker ero-picker--sm' + (selected ? ' is-selected' : '');
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', selected ? 'true' : 'false');
          button.tabIndex = selected ? 0 : -1;
          button.dataset.planId = a.id;
          const label = document.createElement('span');
          label.className = 'bb-freq-btn__label';
          label.textContent = a.label;
          button.appendChild(label);
          if (fallback && a.id === fallback.id && FREQ_POPULAR) {
            const sep = document.createElement('span');
            sep.className = 'bb-freq-btn__sep';
            sep.setAttribute('aria-hidden', 'true');
            sep.textContent = ' - ';
            const tag = document.createElement('span');
            tag.className = 'bb-freq-btn__tag';
            tag.textContent = FREQ_POPULAR;
            button.appendChild(sep);
            button.appendChild(tag);
          }
          freqList.appendChild(button);
        });
        if (list.length) {
          freqList.removeAttribute('aria-disabled');
        } else {
          freqList.setAttribute('aria-disabled', 'true');
        }
      }

      // A keyboard choice keeps focus on the newly selected button, which
      // the rebuild above has just replaced.
      function chooseFrequency(button, fromKeyboard) {
        if (!button || !button.dataset.planId) return;
        activePlanId = button.dataset.planId;
        renderFrequencyOptions(activeProduct());
        updatePrices();
        selectPlan('subscribe');
        if (fromKeyboard && freqList) {
          const chosen = freqList.querySelector('[aria-checked="true"]');
          if (chosen) chosen.focus({ preventScroll: true });
        }
      }

      function dispatchBuyBoxChange() {
        const productData = activeProduct();
        if (!productData) return;
        const allocation = activeAllocation(productData);
        const subscribed = activePlan === 'subscribe' && allocation;
        // An unpinned host has no flavor tile to read a label off, so the
        // product's own title is the label the sticky bar shows.
        const labelNode = root.querySelector('[data-flavor="' + activeFlavor + '"] .bb-flavor__label');
        const flavorLabel = labelNode ? labelNode.textContent.trim() : (productData.title || '');
        // Colour mode's option value ("Nebula Blue"), for the sticky bar's
        // second line. Read off the price card rather than closed over from
        // colorEntries: selectColor() writes that node before it dispatches,
        // so the DOM is already the current answer, and this function is
        // defined well above the colour block. Empty string in flavor mode,
        // where no such node is rendered, so the bag and the Sample Pack
        // publish a field their sticky bar ignores.
        const optionNode = root.querySelector('[data-color-variant-label]');
        document.dispatchEvent(new CustomEvent('ero:buybox:change', {
          detail: {
            key: activeFlavor,
            flavorLabel: flavorLabel,
            optionLabel: optionNode ? optionNode.textContent.trim() : '',
            variantId: productData.variant_id,
            sellingPlanId: subscribed ? allocation.id : null,
            price: subscribed ? allocation.price : productData.price,
            comparePrice: productData.price,
            imageUrl: productData.featured_image
          }
        }));
      }

      function updateBundleBadge() {
        if (!bundleBadge || !products.br || !products.bundle) return;

        const bundleAllocation = activeAllocation(products.bundle);
        const bundlePrice = activePlan === 'subscribe' && bundleAllocation
          ? bundleAllocation.price
          : products.bundle.price;
        const referencePrice = Number(products.br.price || 0) * 2;
        const savings = Math.max(0, referencePrice - Number(bundlePrice || 0));
        // Math unchanged from the July 14 lock: live cents, floored to whole
        // dollars, one line. Only the write is guarded. The amount depends on
        // the plan alone, so a flavor swap recomputes the identical string,
        // and this node carries aria-live: rewriting it re-announced "SAVE
        // $20" on every flavor change for no reason.
        const next = 'SAVE $' + Math.floor(savings / 100) + '!';
        if (bundleBadge.textContent !== next) bundleBadge.textContent = next;
      }

      // Settings-first, matching the Liquid precedence. Shopify's group is
      // named "Subscribe and save", which was overriding the designed label.
      function subscribeLabel(productData, allocation) {
        return catalog.subscribe_fallback
          || productData.selling_plan_title
          || (allocation && allocation.name)
          || '';
      }

      function updatePrices() {
        const productData = activeProduct();
        if (!productData) return;

        const allocation = activeAllocation(productData);
        const subscriptionPrice = allocation ? allocation.price : productData.price;

        // Every write is resolved up front so each node can be compared to
        // what it already shows. Blue Raspberry and Peach Mango carry
        // identical prices, so swapping between them used to re-render
        // unchanged numbers behind a crossfade, which reads as the prices
        // popping for no reason. Only nodes whose text actually differs are
        // written, and only those fade.
        const writes = [
          [subscribeTitle, subscribeLabel(productData, allocation)],
          [subscribeStruck, money(productData.price)],
          [subscribePrice, money(subscriptionPrice)],
          [subscribeServing, perServing(subscriptionPrice, productData.servings)],
          [onetimePrice, FREE_MODE ? 'FREE' : money(productData.price)],
          [onetimeServing, FREE_MODE ? '' : perServing(productData.price, productData.servings)]
        ].filter(function (entry) {
          const node = entry[0];
          if (!node) return false;
          // Compared against the value already SCHEDULED for this node, not
          // its live text. A second swap inside the 200ms window runs before
          // the first one has painted, so live text still reads the value
          // the pending paint is about to replace; comparing against it
          // could skip a write that is genuinely needed and strand the node
          // on the superseded value.
          const shown = priceTargets.has(node) ? priceTargets.get(node) : node.textContent;
          return shown !== entry[1];
        });

        writes.forEach(function (entry) { priceTargets.set(entry[0], entry[1]); });

        // The title is written but never faded, as shipped.
        const fadeNodes = writes
          .map(function (entry) { return entry[0]; })
          .filter(function (node) { return node !== subscribeTitle; });

        const paint = function () {
          writes.forEach(function (entry) { entry[0].textContent = entry[1]; });
          // The badge is deliberately NOT in fadeNodes. Its amount depends
          // only on the active plan, never on the flavor, so on a flavor
          // swap it was fading out and back in around a rewrite of the same
          // string — the reported flicker. selectPlan() calls this directly
          // for the plan toggle, so the July 14 text behavior is unchanged.
          updateBundleBadge();
        };

        // The first paint fills empty placeholders, so fading it would just
        // hold the prices blank for 120ms on load.
        if (!pricesPainted || !wantsMotion() || !fadeNodes.length) {
          paint();
          pricesPainted = true;
        } else {
          fadeNodes.forEach(function (node) { node.style.opacity = '0'; });
          later(function () {
            paint();
            fadeNodes.forEach(function (node) { node.style.opacity = '1'; });
          }, SWAP_MS);
        }

        if (subscribeButton) {
          subscribeButton.classList.toggle('is-disabled', !allocation);
          subscribeButton.setAttribute('aria-disabled', allocation ? 'false' : 'true');
        }

      }

      function updateAtcLabel() {
        if (!atc) return;
        const productData = activeProduct();
        if (!productData) return;

        const labelNode = root.querySelector('[data-flavor="' + activeFlavor + '"] .bb-flavor__label');
        const flavorLabel = (labelNode ? labelNode.textContent.trim() : '') || productData.title || '';

        if (!productData.available) {
          atc.setAttribute('aria-label', flavorLabel + ' is sold out');
          return;
        }

        const allocation = activeAllocation(productData);
        const subscribed = activePlan === 'subscribe' && allocation;
        const price = subscribed ? allocation.price : productData.price;
        atc.setAttribute(
          'aria-label',
          'Add ' + flavorLabel + ' to bag, ' + (FREE_MODE ? 'free' : money(price))
            + (subscribed ? ' subscription' : ' one-time purchase')
        );
      }

      function selectPlan(plan) {
        const productData = activeProduct();
        const allocation = activeAllocation(productData);
        if (plan === 'subscribe' && !allocation) return;

        activePlan = plan;
        planButtons.forEach((button) => {
          const selected = button.dataset.plan === activePlan;
          button.classList.toggle('is-selected', selected);
          // Only the two-card group is a radiogroup. With the subscribe card
          // switched off the remaining card renders without role="radio", and
          // writing aria-checked onto it would invent a state it cannot have.
          if (button.getAttribute('role') === 'radio') {
            button.setAttribute('aria-checked', selected ? 'true' : 'false');
          }
        });

        if (sellingPlanInput) {
          if (activePlan === 'subscribe' && allocation) {
            sellingPlanInput.value = allocation.id;
            sellingPlanInput.disabled = false;
          } else {
            sellingPlanInput.value = '';
            sellingPlanInput.disabled = true;
          }
        }

        updateBundleBadge();
        updateAtcLabel();
        dispatchBuyBoxChange();
      }

      // A full-frame photograph suppresses the accent orb behind the hero.
      // Rides the same code path that moves the active thumb, so the class
      // and the active slide can never disagree.
      function updateFullbleed(index) {
        if (!gallery || !track) return;
        const slides = track.querySelectorAll('[data-gallery-slide]');
        const slide = slides[index];
        gallery.classList.toggle(
          'bb-hero--fullbleed',
          !!slide && slide.dataset.fullbleed === 'true'
        );
        // A Supplement Facts label is shown whole and does not fill the
        // frame, so the frame's photo shadow is off (assets/ero-bb-photo.css).
        gallery.classList.toggle(
          'bb-hero--facts',
          !!slide && slide.dataset.supplementFacts === 'true'
        );
      }

      function updateQuantity(next) {
        quantity = Math.max(1, Number(next) || 1);
        if (qtyInput) qtyInput.value = String(quantity);
        if (qtyValue) qtyValue.textContent = String(quantity);
        qtyButtons.forEach((button) => {
          if (Number(button.dataset.qtyStep) < 0) button.disabled = quantity <= 1;
        });
      }

      // ── Thumb rail overflow (7.4) ─────────────────────────────────────
      // Past five images the rail scrolls: on y where it is a column (the
      // desktop PDP), on x where it is a row (the homepage card and every
      // mobile rail). Only the rail element itself is scrolled; the page
      // never moves.
      const railAxis = () => (
        thumbsNode && window.getComputedStyle(thumbsNode).flexDirection.indexOf('column') === 0 ? 'y' : 'x'
      );

      function railMetrics() {
        const y = railAxis() === 'y';
        return {
          y: y,
          pos: y ? thumbsNode.scrollTop : thumbsNode.scrollLeft,
          client: y ? thumbsNode.clientHeight : thumbsNode.clientWidth,
          size: y ? thumbsNode.scrollHeight : thumbsNode.scrollWidth
        };
      }

      // Scroll mode: more than five thumbs. Keyed on the thumb count, never
      // on measured overflow (five thumbs overflow the four-thumb scroll
      // window, which would flip the mode back and forth). The Liquid sets
      // the same attribute for first paint; this keeps it right after every
      // rebuild.
      function syncRailMode() {
        if (!railFrame || !thumbsNode) return;
        const count = thumbsNode.querySelectorAll('[data-gallery-thumb]').length;
        if (count > 5) railFrame.dataset.railScroll = 'true';
        else delete railFrame.dataset.railScroll;
      }

      // Outside scroll mode both arrows are hidden and out of the tab order
      // and the accessibility tree. In scroll mode both show; the one that
      // cannot scroll further its way is disabled.
      function updateRailArrows() {
        if (!thumbsNode) return;
        const scrollMode = !!railFrame && railFrame.dataset.railScroll === 'true';
        const m = railMetrics();
        const set = (arrow, canMove) => {
          if (!arrow) return;
          arrow.classList.toggle('is-visible', scrollMode);
          if (!scrollMode) {
            arrow.classList.remove('is-disabled');
            arrow.removeAttribute('aria-disabled');
            arrow.setAttribute('aria-hidden', 'true');
            arrow.tabIndex = -1;
            return;
          }
          arrow.setAttribute('aria-hidden', 'false');
          arrow.classList.toggle('is-disabled', !canMove);
          if (canMove) {
            arrow.removeAttribute('aria-disabled');
            arrow.tabIndex = 0;
          } else {
            arrow.setAttribute('aria-disabled', 'true');
            arrow.tabIndex = -1;
          }
        };
        set(railPrev, m.pos > 1);
        set(railNext, m.pos + m.client < m.size - 1);
      }

      // One step is one thumb plus the rail's gap on the scroll axis.
      function scrollRail(direction) {
        if (!thumbsNode) return;
        const first = thumbsNode.querySelector('[data-gallery-thumb]');
        if (!first) return;
        const m = railMetrics();
        const style = window.getComputedStyle(thumbsNode);
        const gap = parseFloat(m.y ? style.rowGap : style.columnGap) || 0;
        const step = (m.y ? first.offsetHeight : first.offsetWidth) + gap;
        const behavior = wantsMotion() ? 'smooth' : 'auto';
        thumbsNode.scrollBy(m.y
          ? { top: direction * step, behavior: behavior }
          : { left: direction * step, behavior: behavior });
      }

      // One mouse-wheel notch moves one thumb, on the desktop column in
      // scroll mode only. A notch is about 100px, more than one thumb step,
      // so native scrolling passed two. Wheel deltas add up to 50px, then one
      // scrollRail step runs and a 250ms cooldown swallows the rest of that
      // notch. The total resets when the direction flips or after 200ms with
      // no wheel event. Pinch zoom, sideways wheels and a rail already at its
      // end are left alone, so the page scrolls as before.
      const WHEEL_STEP_PX = 50;
      const WHEEL_COOLDOWN_MS = 250;
      const WHEEL_IDLE_MS = 200;
      let wheelTotal = 0;
      let wheelLastAt = 0;
      let wheelCooldownUntil = 0;

      function onRailWheel(event) {
        if (!railFrame || railFrame.dataset.railScroll !== 'true' || railAxis() !== 'y') return;
        if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        let delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        else if (event.deltaMode === 2) delta *= thumbsNode.clientHeight;
        const direction = Math.sign(delta);
        if (!direction) return;
        const m = railMetrics();
        if (direction < 0 ? m.pos <= 1 : m.pos + m.client >= m.size - 1) return;

        event.preventDefault();
        const now = window.performance.now();
        const idle = now - wheelLastAt > WHEEL_IDLE_MS;
        wheelLastAt = now;
        if (now < wheelCooldownUntil) return;
        if (idle || Math.sign(wheelTotal) !== direction) wheelTotal = 0;
        wheelTotal += delta;
        if (Math.abs(wheelTotal) < WHEEL_STEP_PX) return;
        wheelTotal = 0;
        wheelCooldownUntil = now + WHEEL_COOLDOWN_MS;
        scrollRail(direction);
      }

      // Scrolls the rail (not the page) just far enough that the active
      // thumb sits fully inside the rail's scroll padding.
      function keepActiveThumbInView(index) {
        if (!thumbsNode) return;
        const thumb = thumbsNode.querySelectorAll('[data-gallery-thumb]')[index];
        if (!thumb) return;
        const m = railMetrics();
        if (m.size <= m.client + 1) return;
        const style = window.getComputedStyle(thumbsNode);
        const padStart = parseFloat(m.y ? style.scrollPaddingTop : style.scrollPaddingLeft) || 0;
        const padEnd = parseFloat(m.y ? style.scrollPaddingBottom : style.scrollPaddingRight) || 0;
        const railRect = thumbsNode.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        const start = (m.y ? thumbRect.top - railRect.top : thumbRect.left - railRect.left) + m.pos;
        const end = start + (m.y ? thumb.offsetHeight : thumb.offsetWidth);
        let target = null;
        if (start - padStart < m.pos) target = start - padStart;
        else if (end + padEnd > m.pos + m.client) target = end + padEnd - m.client;
        if (target === null) return;
        const behavior = wantsMotion() ? 'smooth' : 'auto';
        thumbsNode.scrollTo(m.y
          ? { top: target, behavior: behavior }
          : { left: target, behavior: behavior });
      }

      function updateThumbs(index) {
        activeIndex = index;
        updateFullbleed(index);
        if (!thumbsNode) return;
        const thumbs = Array.from(thumbsNode.querySelectorAll('[data-gallery-thumb]'));
        thumbs.forEach((thumb, thumbIndex) => {
          const selected = thumbIndex === index;
          thumb.classList.toggle('is-active', selected);
          if (selected) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        keepActiveThumbInView(index);
        updateRailArrows();
      }

      function destroyGallery() {
        if (galleryApi && typeof galleryApi.destroy === 'function') {
          galleryApi.destroy();
          if (root._dragInstance) delete root._dragInstance;
        } else if (root._dragInstance) {
          root._dragInstance.kill();
          delete root._dragInstance;
        }
        galleryApi = null;
        // killTweensOf stops animation but leaves the last translate on the
        // track. Desktop stacks slides absolutely and never translates, so a
        // leftover transform from a mobile build would offset the whole
        // gallery after a resize across 990.
        if (window.gsap && track) {
          window.gsap.killTweensOf(track);
          window.gsap.set(track, { clearProps: 'all' });
        } else if (track) {
          track.style.transform = '';
        }
      }

      function buildGallery(productData) {
        if (!track || !viewport) return;
        destroyGallery();

        const media = Array.isArray(productData.media) ? productData.media : [];
        // Re-resolved here, not just in Liquid: a flavor swap can land on a
        // product with a different image count, and the rail and hero height
        // follow the count.
        root.dataset.railShort = media.length <= 4 ? 'true' : 'false';
        track.replaceChildren();

        media.forEach((mediaItem, index) => {
          const slide = document.createElement('div');
          const image = document.createElement('img');
          slide.className = 'bb-slide';
          slide.dataset.gallerySlide = '';
          slide.dataset.fullbleed = mediaItem.fullbleed ? 'true' : 'false';
          // Alt text starting "Supplement Facts" (flag from the Liquid catalog).
          if (mediaItem.facts) slide.dataset.supplementFacts = 'true';
          image.className = 'bb-slide__image';
          image.src = mediaItem.url;
          // Same ladder the Liquid slides ship with, so the rebuilt slides
          // resolve at the browser's real DPR instead of upscaling a 900w.
          if (mediaItem.srcset) {
            image.srcset = mediaItem.srcset;
            image.sizes = '(min-width: 990px) 46vw, 100vw';
          }
          image.alt = mediaItem.alt || productData.title || '';
          image.loading = index === 0 ? 'eager' : 'lazy';
          image.draggable = false;
          // Full-frame photo: crop around the focal point set in Shopify admin.
          // A Supplement Facts label is never cropped and stays centred.
          if (mediaItem.fullbleed && mediaItem.focal && !mediaItem.facts) image.style.objectPosition = mediaItem.focal;
          slide.appendChild(image);
          track.appendChild(slide);
        });

        // One slot per real image, matching the Liquid rail. This used to
        // loop to a fixed slot count and paint the surplus as a disabled
        // empty state, which showed as placeholder thumbs on any product
        // with fewer images than the slot count.
        if (thumbsNode) {
          thumbsNode.replaceChildren();
          media.forEach(function (mediaItem, index) {
            const thumb = document.createElement('button');
            thumb.type = 'button';
            thumb.dataset.galleryThumb = '';
            thumb.dataset.index = String(index);
            thumb.dataset.fullbleed = mediaItem.fullbleed ? 'true' : 'false';
            // Picker module classes live HERE: this rebuild runs at init
            // through selectFlavor(initialFlavor), on every flavor or colour
            // swap and on every 990 boundary crossing, so it is the producer
            // of every rendered thumb. The Liquid seed carries the same pair so
            // the two never disagree. Thumbs are the lift-only kind.
            thumb.className = 'bb-thumb ero-picker ero-picker--lift' + (index === 0 ? ' is-active' : '');

            const mediaWrap = document.createElement('span');
            mediaWrap.className = 'bb-thumb__media';
            mediaWrap.dataset.eroDecor = '';

            const image = document.createElement('img');
            image.className = 'bb-thumb__image';
            image.src = mediaItem.url;
            image.alt = '';
            image.loading = 'lazy';
            image.draggable = false;
            if (mediaItem.fullbleed && mediaItem.focal) image.style.objectPosition = mediaItem.focal;
            mediaWrap.appendChild(image);
            thumb.setAttribute('aria-label', 'Show image ' + (index + 1));
            if (index === 0) thumb.setAttribute('aria-current', 'true');

            const glow = document.createElement('span');
            glow.className = 'bb-thumb__glow';
            glow.dataset.eroDecor = '';
            glow.setAttribute('aria-hidden', 'true');

            thumb.appendChild(mediaWrap);
            thumb.appendChild(glow);
            thumbsNode.appendChild(thumb);
          });
          // The rebuilt rail starts on thumb 0: set the scroll mode for the
          // new thumb count first, then bring thumb 0 back into view if the
          // old rail was scrolled, and re-read which arrows apply.
          syncRailMode();
          keepActiveThumbInView(0);
          updateRailArrows();
        }

        // Covers both the navigable and the single-image paths below, which
        // otherwise never reach commit().
        updateFullbleed(0);

        // Desktop crossfades in place: slides are stacked by CSS and the
        // active one is a class toggle, so Draggable never runs on this side
        // of the breakpoint and the track is never translated. Mobile falls
        // through to the shipped Draggable path untouched.
        if (desktopQuery.matches) {
          const desktopSlides = Array.from(track.querySelectorAll('[data-gallery-slide]'));
          desktopSlides.forEach(function (slide, index) {
            slide.classList.toggle('is-current', index === 0);
          });

          galleryApi = {
            goTo: function (index) {
              if (!desktopSlides.length) return;
              let target = index;
              if (target < 0) target = desktopSlides.length - 1;
              if (target >= desktopSlides.length) target = 0;
              desktopSlides.forEach(function (slide, slideIndex) {
                slide.classList.toggle('is-current', slideIndex === target);
              });
              updateThumbs(target);
            },
            destroy: function () {}
          };

          updateThumbs(0);
          if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
            window.ScrollTrigger.refresh();
          }
          return;
        }

        const hasNavigation = media.length > 1;

        if (!hasNavigation || !window.gsap || !window.Draggable) {
          if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
            window.ScrollTrigger.refresh();
          }
          return;
        }

        window.gsap.registerPlugin(window.Draggable);
        if (window.InertiaPlugin) window.gsap.registerPlugin(window.InertiaPlugin);

        const slides = Array.from(track.querySelectorAll('[data-gallery-slide]'));
        // One step is the distance between two slide starts, read to a
        // fraction of a pixel. offsetWidth rounds (351u is 365.04 px at 390
        // wide, 402.48 at 430) and the error piled up per slide into a
        // visible sliver of the previous photo. The step includes the
        // track gap (ero-bb-photo.css).
        const firstRect = slides[0].getBoundingClientRect();
        const step = slides.length > 1
          ? slides[1].getBoundingClientRect().left - firstRect.left
          : firstRect.width;
        const snaps = slides.map((slide, index) => -index * step);
        let currentIndex = 0;
        let dragStartIndex = 0;

        const nearestIndex = (value) => {
          let nearest = 0;
          let distance = Infinity;
          snaps.forEach((snapValue, index) => {
            const nextDistance = Math.abs(value - snapValue);
            if (nextDistance < distance) {
              distance = nextDistance;
              nearest = index;
            }
          });
          return nearest;
        };

        const commit = (index) => {
          currentIndex = Math.max(0, Math.min(slides.length - 1, index));
          updateThumbs(currentIndex);
        };

        const drag = window.Draggable.create(track, {
          type: 'x',
          inertia: true,
          edgeResistance: 0.85,
          dragResistance: 0.05,
          minimumMovement: 6,
          dragClickables: true,
          allowNativeTouchScrolling: true,
          bounds: { minX: snaps[snaps.length - 1], maxX: snaps[0] },
          snap: {
            x: function (endValue) {
              let bestIndex = dragStartIndex;
              let bestDistance = Math.abs(endValue - snaps[dragStartIndex]);
              const minimumIndex = Math.max(0, dragStartIndex - 2);
              const maximumIndex = Math.min(snaps.length - 1, dragStartIndex + 2);
              for (let index = minimumIndex; index <= maximumIndex; index += 1) {
                const distance = Math.abs(endValue - snaps[index]);
                if (distance < bestDistance) {
                  bestDistance = distance;
                  bestIndex = index;
                }
              }
              return snaps[bestIndex];
            }
          },
          onDragStart: function () {
            dragStartIndex = currentIndex;
          },
          onThrowUpdate: function () {
            const nextIndex = nearestIndex(this.x);
            if (nextIndex !== currentIndex) commit(nextIndex);
          },
          onThrowComplete: function () {
            commit(nearestIndex(this.x));
          }
        })[0];

        root._dragInstance = drag;

        const goTo = (index) => {
          let targetIndex = index;
          if (targetIndex < 0) targetIndex = slides.length - 1;
          if (targetIndex >= slides.length) targetIndex = 0;
          commit(targetIndex);
          window.gsap.to(track, {
            x: snaps[targetIndex],
            duration: motionDuration,
            ease: 'power2.out',
            overwrite: true,
            onComplete: function () {
              drag.update();
            }
          });
        };

        galleryApi = {
          goTo: goTo,
          destroy: function () {
            window.gsap.killTweensOf(track);
            drag.kill();
          }
        };

        window.gsap.set(track, { x: snaps[0] });
        if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
          window.ScrollTrigger.refresh();
        }
      }

      function selectFlavor(flavor) {
        const productData = products[flavor];
        const accent = ACCENTS[flavor];
        if (!productData || !accent) return;

        activeFlavor = flavor;
        updateRating(flavor);
        updateReviews(flavor);
        root.style.setProperty('--ero-accent-color', accent.color);
        root.style.setProperty('--ero-accent-rgb', accent.rgb);
        if (logoGlow && accent.logoFilter) {
          logoGlow.setAttribute('filter', 'url(#' + accent.logoFilter + ')');
        }
        if (logoGlowMobile && accent.logoFilter) {
          logoGlowMobile.setAttribute('filter', 'url(#' + accent.logoFilter + 'Mobile)');
        }

        flavorButtons.forEach((button) => {
          const selected = button.dataset.flavor === activeFlavor;
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        if (nameNode) nameNode.textContent = productData.title;
        if (variantInput) variantInput.value = productData.variant_id;
        if (atc) atc.disabled = !productData.available;
        atcLabels.forEach(function (node) {
          node.textContent = productData.available
            ? (node.dataset.atcLabelText || ATC_LABEL)
            : 'SOLD OUT';
        });

        if (activePlan === 'subscribe' && !activeAllocation(productData)) {
          activePlan = 'onetime';
        }
        renderFrequencyOptions(productData);

        updatePrices();
        selectPlan(activePlan);

        // Desktop swaps the whole gallery on a flavor change, so the fade is
        // on the viewport rather than an individual slide. Mobile rebuilds
        // instantly, as shipped.
        if (viewport && desktopQuery.matches && wantsMotion()) {
          viewport.style.opacity = '0';
          later(function () {
            buildGallery(productData);
            viewport.style.opacity = '1';
          }, SWAP_MS);
        } else {
          if (viewport) viewport.style.opacity = '';
          buildGallery(productData);
        }

        updateAtcLabel();
      }

      // ── Colour mode ──────────────────────────────────────────────────
      // One product, one option, three variants, keyed to the variants
      // themselves rather than to pinned products. Nothing below runs in
      // flavor mode: catalog.colors is an empty array there and no card
      // carries [data-color-card], so both loops are no-ops on the bag and
      // the Sample Pack.
      const colorCards = Array.from(root.querySelectorAll('[data-color-card]'));
      const colorEntries = Array.isArray(catalog.colors) ? catalog.colors : [];
      const colorVariantLabel = root.querySelector('[data-color-variant-label]');

      function selectColor(variantId) {
        const entry = colorEntries.filter(function (candidate) {
          return String(candidate.id) === String(variantId);
        })[0];
        if (!entry) return;

        colorCards.forEach(function (card) {
          const selected = String(card.dataset.variantId) === String(entry.id);
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-checked', selected ? 'true' : 'false');
        });

        if (variantInput) variantInput.value = entry.id;
        if (colorVariantLabel) colorVariantLabel.textContent = entry.label;
        if (atc) atc.disabled = !entry.available;
        atcLabels.forEach(function (node) {
          node.textContent = entry.available
            ? (node.dataset.atcLabelText || ATC_LABEL)
            : 'SOLD OUT';
        });

        // Written THROUGH the catalog entry rather than straight onto the
        // price node, so updatePrices() keeps ownership of the number and
        // its crossfade, its equality guard still compares like for like,
        // and no price is ever hardcoded on this path. Whatever Shopify
        // reported for the variant is what renders.
        const hostData = products[activeFlavor];
        if (hostData) {
          hostData.price = entry.price;
          hostData.variant_id = entry.id;
          hostData.available = entry.available;
          if (entry.image) hostData.featured_image = entry.image;
        }
        updatePrices();

        // Reuses the rail's own navigation instead of rebuilding the
        // gallery: goTo covers the desktop crossfade and the mobile
        // Draggable path both, and syncs the thumbs for free. -1 means the
        // variant has no image in the rail, so the gallery stays put.
        if (galleryApi && typeof galleryApi.goTo === 'function' && entry.media_index >= 0) {
          galleryApi.goTo(entry.media_index);
        }

        dispatchBuyBoxChange();
      }

      // Published so a second picker elsewhere on the page — the sticky
      // bar's colour panel — can drive THIS one instead of keeping its own
      // copy of the selection logic. Everything downstream (price, thumb,
      // gallery, ero:buybox:change) then runs exactly as a click here does.
      root._eroSelectColor = selectColor;

      colorCards.forEach(function (card) {
        const activate = function () {
          if (card.dataset.unavailable === 'true') return;
          selectColor(card.dataset.variantId);
        };
        card.addEventListener('pointerup', activate, { signal: signal });
        card.addEventListener('keydown', function (event) {
          if (!isActivationKey(event)) return;
          event.preventDefault();
          activate();
        }, { signal: signal });
      });

      flavorButtons.forEach((button) => {
        const activate = () => selectFlavor(button.dataset.flavor);
        button.addEventListener('pointerup', activate, { signal: signal });
        button.addEventListener('keydown', (event) => {
          if (!isActivationKey(event)) return;
          event.preventDefault();
          activate();
        }, { signal: signal });
      });

      planButtons.forEach((button) => {
        const activate = () => selectPlan(button.dataset.plan);
        button.addEventListener('pointerup', activate, { signal: signal });
        button.addEventListener('keydown', (event) => {
          if (event.target.closest && event.target.closest('[data-frequency]')) return;
          if (!isActivationKey(event)) return;
          event.preventDefault();
          activate();
        }, { signal: signal });
      });

      // Frequency buttons, a radio group. Pointer Events only. A tap on a
      // button also bubbles to the Subscribe card's own pointerup, which
      // selects the card: intended. The card's keydown guard ignores keys
      // from inside [data-frequency], so the arrows here never reach it.
      if (freqList) {
        freqList.addEventListener('pointerup', (event) => {
          const button = event.target.closest ? event.target.closest('.bb-freq-btn') : null;
          chooseFrequency(button, false);
        }, { signal: signal });
        freqList.addEventListener('keydown', (event) => {
          const buttons = Array.from(freqList.querySelectorAll('.bb-freq-btn'));
          if (!buttons.length) return;
          const current = event.target.closest ? event.target.closest('.bb-freq-btn') : null;
          const index = buttons.indexOf(current);
          let next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            next = buttons[(index + 1) % buttons.length];
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            next = buttons[(index - 1 + buttons.length) % buttons.length];
          } else if (event.key === 'Home') {
            next = buttons[0];
          } else if (event.key === 'End') {
            next = buttons[buttons.length - 1];
          } else if (event.key === 'Enter' || event.key === ' ') {
            next = current;
          }
          if (!next) return;
          event.preventDefault();
          chooseFrequency(next, true);
        }, { signal: signal });
      }

      // Writes into the quantity input the form has always posted. These
      // listeners have never been viewport-gated; before Aug 26 the buttons
      // were simply display:none below 990, so binding them was harmless.
      // The mobile row now shows the same control and reuses them as-is.
      qtyButtons.forEach((button) => {
        const activate = () => updateQuantity(quantity + Number(button.dataset.qtyStep));
        button.addEventListener('pointerup', activate, { signal: signal });
        button.addEventListener('keydown', (event) => {
          if (!isActivationKey(event)) return;
          event.preventDefault();
          activate();
        }, { signal: signal });
      });

      if (thumbsNode) {
        // Pointer Events only. iOS synthesizes a mouse pair after touch, so
        // binding mousedown/touchstart alongside these would double-fire.
        const releasePress = (thumb) => {
          if (!thumb) return;
          const timer = pressTimers.get(thumb);
          if (timer) {
            window.clearTimeout(timer);
            pressTimers.delete(thumb);
          }
          thumb.classList.remove('is-pressed');
        };

        thumbsNode.addEventListener('pointerdown', (event) => {
          const thumb = event.target.closest('[data-gallery-thumb]');
          if (!thumb || thumb.disabled) return;
          releasePress(thumb);
          thumb.classList.add('is-pressed');
          pressTimers.set(thumb, window.setTimeout(() => {
            thumb.classList.remove('is-pressed');
            pressTimers.delete(thumb);
          }, THUMB_PRESS_MS));
        }, { signal: signal });

        thumbsNode.addEventListener('pointercancel', (event) => {
          releasePress(event.target.closest('[data-gallery-thumb]'));
        }, { signal: signal });

        thumbsNode.addEventListener('pointerup', (event) => {
          const thumb = event.target.closest('[data-gallery-thumb]');
          if (!thumb || thumb.disabled || !galleryApi) return;
          galleryApi.goTo(Number(thumb.dataset.index));
        }, { signal: signal });

        thumbsNode.addEventListener('keydown', (event) => {
          if (!isActivationKey(event)) return;
          const thumb = event.target.closest('[data-gallery-thumb]');
          if (!thumb || thumb.disabled || !galleryApi) return;
          event.preventDefault();
          galleryApi.goTo(Number(thumb.dataset.index));
        }, { signal: signal });

        // Rail overflow arrows, Pointer Events plus Enter / Space. The
        // arrows re-read on every rail scroll and on resize, which also
        // covers the 990 crossing (the rail changes axis there). The wheel
        // handler is bound once on the rail itself (a flavor rebuild only
        // swaps the thumbs inside it) and goes with the same signal, so
        // nothing stacks; its arrow state follows from the scroll listener.
        thumbsNode.addEventListener('scroll', updateRailArrows, { passive: true, signal: signal });
        thumbsNode.addEventListener('wheel', onRailWheel, { passive: false, signal: signal });
        window.addEventListener('resize', updateRailArrows, { passive: true, signal: signal });
        [[railPrev, -1], [railNext, 1]].forEach(function (pair) {
          const arrow = pair[0];
          const direction = pair[1];
          if (!arrow) return;
          arrow.addEventListener('pointerup', function () {
            scrollRail(direction);
          }, { signal: signal });
          arrow.addEventListener('keydown', function (event) {
            if (!isActivationKey(event)) return;
            event.preventDefault();
            scrollRail(direction);
          }, { signal: signal });
        });
      }

      // ── Zoom lightbox ────────────────────────────────────────────────
      // Desktop only for now; mobile gets its lightbox in the queued rework.
      function renderLightboxImage(index) {
        const productData = activeProduct();
        const media = productData && Array.isArray(productData.media) ? productData.media : [];
        if (!media.length || !lightboxImage) return;

        let target = index;
        if (target < 0) target = media.length - 1;
        if (target >= media.length) target = 0;
        lightboxIndex = target;

        const item = media[target];
        lightboxImage.src = item.url_xl || item.url;
        lightboxImage.alt = item.alt || productData.title || '';
      }

      function isLightboxOpen() {
        return !!lightbox && lightbox.classList.contains('is-open');
      }

      function openLightbox() {
        if (!lightbox || !desktopQuery.matches || isLightboxOpen()) return;
        renderLightboxImage(activeIndex);
        lightbox.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        if (lightboxClose) lightboxClose.focus();
      }

      function closeLightbox() {
        if (!isLightboxOpen()) return;
        lightbox.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        // Arrow keys may have moved past the slide the hero is showing.
        if (galleryApi && typeof galleryApi.goTo === 'function' && lightboxIndex !== activeIndex) {
          galleryApi.goTo(lightboxIndex);
        }
        if (zoomButton) zoomButton.focus();
      }

      if (zoomButton) {
        zoomButton.addEventListener('pointerup', openLightbox, { signal: signal });
        zoomButton.addEventListener('keydown', (event) => {
          if (!isActivationKey(event)) return;
          event.preventDefault();
          openLightbox();
        }, { signal: signal });
      }

      if (lightbox) {
        // Backdrop only: a click that lands on the image or the close button
        // has a different target and must not fall through to this.
        lightbox.addEventListener('pointerup', (event) => {
          if (event.target === lightbox) closeLightbox();
        }, { signal: signal });
      }

      if (lightboxClose) {
        lightboxClose.addEventListener('pointerup', closeLightbox, { signal: signal });
        lightboxClose.addEventListener('keydown', (event) => {
          if (!isActivationKey(event)) return;
          event.preventDefault();
          closeLightbox();
        }, { signal: signal });
      }

      document.addEventListener('keydown', (event) => {
        if (!isLightboxOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeLightbox();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          renderLightboxImage(lightboxIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          renderLightboxImage(lightboxIndex + 1);
        }
      }, { signal: signal });

      // Crossing 990 switches the gallery between the desktop crossfade and
      // the mobile Draggable track, so it has to be rebuilt or the two modes
      // mix: stacked slides with a translated track, or Draggable snapping
      // against absolutely positioned children.
      desktopQuery.addEventListener('change', () => {
        const productData = activeProduct();
        if (!productData) return;
        if (viewport) viewport.style.opacity = '';
        closeLightbox();
        buildGallery(productData);
      }, { signal: signal });

      root._eroBuyBoxCleanup = function () {
        delete root._eroSelectColor;
        delete root._eroReviewSourceIds;
        controller.abort();
        destroyGallery();
        pressTimers.forEach((timer) => window.clearTimeout(timer));
        pressTimers.clear();
        timers.forEach((timer) => window.clearTimeout(timer));
        timers.clear();
        ratingToken += 1;
        reviewToken += 1;
        window.clearTimeout(reviewTimer);
        if (reviewObserver) reviewObserver.disconnect();
        document.documentElement.style.overflow = '';
        if (lightbox && lightbox.parentNode) lightbox.parentNode.removeChild(lightbox);
        if (motionMedia) motionMedia.revert();
        delete root._eroBuyBoxCleanup;
      };

      updateQuantity(1);
      if (ratingRow && window.EroLoox) {
        ratingRow.classList.add('is-rating-pending');
        const ratingPendingTimer = window.setTimeout(clearRatingPending, 2500);
        timers.add(ratingPendingTimer);
        // Warm Blue Raspberry and Peach Mango on the bag PDP so a flavor
        // swap paints from cache instead of waiting on the network.
        if (products.bundle) window.EroLoox.combinedRating(LOOX.combined_ids);
      }
      selectFlavor(initialFlavor);
      selectPlan(activePlan);
      syncRailMode();
      updateRailArrows();
    }

    function initAll(scope) {
      const context = scope || document;
      if (context.matches && context.matches('[data-ero-pdp-buy-box]')) initRoot(context);
      context.querySelectorAll('[data-ero-pdp-buy-box]').forEach(initRoot);
    }

    if (document.readyState === 'complete') {
      initAll(document);
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        initAll(document);
      }, { once: true });
    }

    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      const root = event.target.matches && event.target.matches('[data-ero-pdp-buy-box]')
        ? event.target
        : event.target.querySelector('[data-ero-pdp-buy-box]');
      if (root && root._eroBuyBoxCleanup) root._eroBuyBoxCleanup();
    });
  })();
